import { SimpleObservableValue } from '../../../stores/SimpleObservableValue'
import { Cubit } from '../../core'
import type { AssignNextbookCubit } from '../AssignNextbookCubit'
import { fieldsSchema, storageSchema } from '../schemas'
import type { StorageProvider } from '../types'

export class AssignNextbookStorageController extends Cubit {
  cubit: AssignNextbookCubit
  storage: StorageProvider | undefined
  isLoaded = new SimpleObservableValue(false)

  constructor(cubit: AssignNextbookCubit, storage?: StorageProvider) {
    super()
    this.cubit = cubit
    this.storage = storage
  }

  initialize() {
    this.addReaction({
      whenThisChanges: () => this.key,
      thenRunThisCode: () => {
        this.load()
      },
    })
  }

  get key() {
    const sectionId = this.cubit.form.fields.get('sectionId')
    return `assignNextbookFields-${this.cubit.teachingPlanCubit.teachingPlanId}-${sectionId || 'new'}`
  }

  async clearOldData() {
    if (!this.storage) return

    try {
      for (const key of Object.keys(this.storage)) {
        if (key.startsWith('assignNextbookFields-')) {
          const stored = await this.storage.getItem(key)
          if (!stored) {
            this.storage.removeItem(key)
            continue
          }

          const parsed = storageSchema.safeParse(JSON.parse(stored))

          if (!parsed.success) {
            // it's malformed, remove
            this.storage.removeItem(key)
            console.error('Failed to parse stored fields:', parsed.error)
            continue
          }

          if (parsed.data.timestamp < Date.now() - 30 * 24 * 60 * 60 * 1000) {
            // If the stored data is older than 30 days, clear it
            this.storage.removeItem(key)
            continue
          }
        }
      }
    } catch (error) {
      console.error('Failed to clear old data:', error)
    }
  }

  async load() {
    try {
      if (!this.storage) return

      const storedFields = await this.storage.getItem(this.key)
      if (storedFields) {
        const parsedFields = JSON.parse(storedFields)
        const parsed = storageSchema.safeParse(parsedFields)

        if (!parsed.success) {
          this.storage?.removeItem(this.key)
          console.error('Failed to parse stored fields:', parsed.error)
          return
        }

        if (parsed.data.timestamp < Date.now() - 30 * 24 * 60 * 60 * 1000) {
          // If the stored data is older than 30 days, clear it
          this.storage?.removeItem(this.key)
          return
        }

        this.cubit.form.fields.replace(parsed.data.payload)
      }
    } catch (error) {
      console.error('Failed to parse stored fields:', error)
    } finally {
      this.isLoaded.setValue(true)
    }

    this.clearOldData()
  }

  async save() {
    if (!this.storage) return

    try {
      const fields = Object.fromEntries(this.cubit.form.fields.map.entries())

      // validate the fields before saving them
      const parsed = fieldsSchema.safeParse(fields)

      if (!parsed.success) {
        console.error('Failed to parse fields for storage:', parsed.error)
        return
      }

      const payload = {
        ...parsed.data,
        assignments: parsed.data.assignments?.map((assignment) => ({
          ...assignment,
          // convert DateTime to ISO string
          startTime: assignment.startTime.toISO(),
          endTime: assignment.endTime.toISO(),
        })),
      }

      await this.storage.setItem(
        this.key,
        JSON.stringify({
          version: 1,
          timestamp: Date.now(),
          payload: payload,
        })
      )
    } catch (error) {
      console.error('Failed to save fields to storage:', error)
    }
  }

  async clear() {
    if (!this.storage) return

    this.storage.removeItem(this.key)
  }
}
